import React from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import DisclaimerBanner from "../components/DisclaimerBanner";
import SiteFooter from "../components/SiteFooter";
import { Building2, ArrowRight, Library, Scale, ScrollText, HandHeart } from "lucide-react";

/**
 * Resource Center hub.
 * First card: Business & Registered Agent Search (the 50-state directory).
 * Future cards (fee waivers, court self-help, etc.) will slot in below.
 */
export default function Resources() {
  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[1200px] mx-auto px-6 py-12 md:py-16" data-testid="resources-page">
        <header className="mb-10">
          <div className="label-tiny mb-3">Resource Center</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-slate-900 leading-tight max-w-3xl">
            <Library className="inline-block w-9 h-9 mr-2 -mt-2 text-slate-700" strokeWidth={1.75} />
            Curated resources for preparing your case.
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Links to official government sources and organizational tools to help you
            gather accurate information. ProSe.Claims provides links and organizational
            assistance only. Confirm important information with the official agency,
            court, or a licensed attorney.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0" data-testid="resource-cards">
          <ResourceCard
            to="/resources/business-search"
            icon={Building2}
            eyebrow="Directory"
            tint="blue"
            title="Business & Registered Agent Search"
            body="Official public business-search databases for all 50 states and Washington, D.C. Locate exact legal names, entity types, formation states, statuses, registered agents, and more."
            available
          />
          <ResourceCard
            to="/resources/fee-waivers"
            icon={Scale}
            eyebrow="Directory"
            tint="orange"
            title="Court Fee Waivers"
            body="Federal + all 50 states + D.C. Official fee-waiver / in-forma-pauperis pages, form numbers, plain-English explanations, and eligibility signals."
            available
          />
          <ResourceCard
            to="/resources/legal-library"
            icon={ScrollText}
            eyebrow="Library"
            tint="teal"
            title="Legal Research Library"
            body="Curated official links to constitutions, statutes, rules of civil procedure, and court forms across federal courts and every state. Filter by jurisdiction, resource type, and claim category."
            available
          />
          <ResourceCard
            to="/resources/free-legal-help"
            icon={HandHeart}
            eyebrow="Directory"
            tint="green"
            title="Free Legal Help by State"
            body="LSC-grantee legal aid organizations, statewide self-help portals, and intake hotlines for every U.S. state, D.C., and a national LSC selector."
            available
          />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

const TINTS = {
  blue:   { bg: "bg-blue-50",    fg: "text-blue-600",    hoverBg: "group-hover:bg-blue-100",    hoverFg: "group-hover:text-blue-700" },
  orange: { bg: "bg-orange-50",  fg: "text-orange-600",  hoverBg: "group-hover:bg-orange-100",  hoverFg: "group-hover:text-orange-700" },
  teal:   { bg: "bg-teal-50",    fg: "text-teal-700",    hoverBg: "group-hover:bg-teal-100",    hoverFg: "group-hover:text-teal-800" },
  green:  { bg: "bg-emerald-50", fg: "text-emerald-700", hoverBg: "group-hover:bg-emerald-100", hoverFg: "group-hover:text-emerald-800" },
  slate:  { bg: "bg-slate-100",  fg: "text-slate-800",   hoverBg: "group-hover:bg-slate-200",   hoverFg: "group-hover:text-slate-900" },
};

function ResourceCard({ to, icon: Icon, eyebrow, title, body, tint = "slate", available = false }) {
  const t = TINTS[tint] || TINTS.slate;
  const inner = (
    <div
      className={`group h-full flex flex-col border border-slate-200 -mt-px -ml-px bg-white p-8 md:p-10 transition-[transform,box-shadow,border-color] duration-300 ${
        available
          ? "hover:z-10 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-16px_rgba(15,23,42,0.20)] hover:border-slate-300"
          : "opacity-70"
      }`}
      data-testid={`resource-card-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="font-mono-tech text-xs text-slate-500 mb-5 uppercase tracking-widest">{eyebrow}</div>
      {Icon && (
        <div
          className={`mb-6 inline-flex items-center justify-center w-14 h-14 rounded-xl ${t.bg} ${t.fg} ${t.hoverBg} ${t.hoverFg} transition-[background-color,color,transform] duration-300 group-hover:-translate-y-0.5`}
        >
          <Icon strokeWidth={1.75} className="w-7 h-7" />
        </div>
      )}
      <h3 className="font-serif-display text-2xl text-slate-900 leading-tight mb-3">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed flex-1">{body}</p>
      {available ? (
        <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-900 underline underline-offset-4 group-hover:text-slate-700">
          Open directory <ArrowRight className="w-4 h-4" />
        </div>
      ) : (
        <div className="mt-6 label-tiny text-slate-500">Available soon</div>
      )}
    </div>
  );
  return available && to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}
